import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Eye, EyeOff, Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import { adminApi, formatApiError } from "../../lib/adminApi";
import { mediaUrl } from "../../lib/content";
import MediaUpload from "./MediaUpload";

const CATEGORY_SUGGESTIONS = [
  "Projection Advertising",
  "Projection Mapping",
  "Architectural Mapping",
  "Immersive Experience",
  "Live Event",
  "Product Launch",
  "Permanent Installation",
];

const inputCls =
  "w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-white/50 transition-colors";

const emptyForm = {
  title: "",
  location: "",
  category: "",
  description: "",
  cover: null,
  video: null,
  images: [],
  published: true,
};

const toForm = (p) => ({
  title: p.title || "",
  location: p.location || "",
  category: p.category || "",
  description: p.description || "",
  cover: p.cover || null,
  video: (p.media || []).find((m) => m.type === "video")?.src || null,
  images: (p.media || []).filter((m) => m.type === "image").map((m) => m.src),
  published: p.published !== false,
});

const toPayload = (f) => ({
  title: f.title.trim(),
  location: f.location.trim(),
  category: f.category.trim(),
  description: f.description.trim(),
  cover: f.cover,
  media: [
    ...(f.video ? [{ type: "video", src: f.video }] : []),
    ...f.images.map((src) => ({ type: "image", src })),
  ],
  published: f.published,
});

export const ProjectsTab = () => {
  const [projects, setProjects] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | project
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await adminApi.get("/admin/projects");
    setProjects(data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, []);

  const startEdit = (p) => {
    setEditing(p);
    setForm(p === "new" ? emptyForm : toForm(p));
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Please add a project title.");
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing === "new") {
        await adminApi.post("/admin/projects", payload);
        toast.success("Project added.");
      } else {
        await adminApi.put(`/admin/projects/${editing.id}`, payload);
        toast.success("Project saved.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const move = async (index, dir) => {
    const next = [...projects];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setProjects(next);
    try {
      await adminApi.post("/admin/projects/reorder", { ids: next.map((p) => p.id) });
      toast.success("Order saved.");
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const togglePublished = async (p) => {
    try {
      await adminApi.put(`/admin/projects/${p.id}`, { ...toPayload(toForm(p)), published: !p.published });
      toast.success(p.published ? "Project hidden from the site." : "Project published.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" permanently?`)) return;
    try {
      await adminApi.delete(`/admin/projects/${p.id}`);
      toast.success("Project deleted.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  if (!projects) {
    return <div className="py-16 text-center text-white/40 text-sm">Loading projects…</div>;
  }

  if (editing) {
    return (
      <div data-testid="project-editor" className="border border-white/10 bg-[#0d0d10] p-5 md:p-8">
        <h3 className="font-heading text-lg font-light text-white mb-6">
          {editing === "new" ? "Add Project" : `Edit — ${editing.title}`}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Project title *</span>
            <input data-testid="project-title-input" className={inputCls} value={form.title} onChange={set("title")} placeholder="e.g. ENSO × Brick Lane" />
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Location</span>
            <input data-testid="project-location-input" className={inputCls} value={form.location} onChange={set("location")} placeholder="e.g. London" />
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Category</span>
            <input data-testid="project-category-input" className={inputCls} list="category-options" value={form.category} onChange={set("category")} placeholder="e.g. Projection Mapping" />
            <datalist id="category-options">
              {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                data-testid="project-published-toggle"
                className="h-4 w-4 accent-[#c8b99d]"
              />
              <span className="text-sm text-white/70">Published (visible on the site)</span>
            </label>
          </div>
          <div className="md:col-span-2">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Description</span>
            <textarea data-testid="project-description-input" rows={3} className={`${inputCls} resize-none`} value={form.description} onChange={set("description")} placeholder="A short line shown in the project viewer." />
          </div>
          <MediaUpload label="Cover image" kind="image" value={form.cover} onChange={(v) => setForm((f) => ({ ...f, cover: v }))} testId="cover-upload" />
          <MediaUpload label="Video (optional)" kind="video" value={form.video} onChange={(v) => setForm((f) => ({ ...f, video: v }))} testId="video-upload" />
          <div className="md:col-span-2">
            <MediaUpload label="Gallery images" kind="image" value={null} onChange={(v) => v && setForm((f) => ({ ...f, images: [...f.images, v] }))} testId="gallery-upload" />
            {form.images.length > 0 && (
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                {form.images.map((src, i) => (
                  <div key={src} className="relative border border-white/15 bg-black/40">
                    <img src={mediaUrl(src)} alt={`Gallery ${i + 1}`} className="w-full h-20 object-cover" />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, images: f.images.filter((x) => x !== src) }))}
                      data-testid={`gallery-remove-${i}`}
                      className="absolute top-1 right-1 bg-black/70 border border-white/20 p-1 text-white/70 hover:text-white"
                      aria-label="Remove image"
                    >
                      <XSafe />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={save}
            disabled={saving}
            data-testid="project-save-button"
            className="inline-flex items-center gap-2 bg-white text-black px-7 py-3 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-[#c8b99d] transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? "Saving…" : "Save Project"}
          </button>
          <button
            onClick={() => setEditing(null)}
            data-testid="project-cancel-button"
            className="border border-white/25 px-7 py-3 text-[11px] uppercase tracking-[0.25em] text-white/70 hover:text-white hover:border-white transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-white/40">{projects.length} project{projects.length === 1 ? "" : "s"}</p>
        <button
          onClick={() => startEdit("new")}
          data-testid="add-project-button"
          className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-[#c8b99d] transition-colors"
        >
          <Plus size={14} /> Add Project
        </button>
      </div>
      <div className="space-y-3">
        {projects.map((p, i) => (
          <div
            key={p.id}
            data-testid={`project-row-${i}`}
            className="flex flex-wrap items-center gap-4 border border-white/10 bg-[#0d0d10] p-4"
          >
            {p.cover ? (
              <img src={mediaUrl(p.cover)} alt={p.title} className="h-14 w-20 object-cover border border-white/10" />
            ) : (
              <div className="h-14 w-20 border border-white/10 bg-black/40" />
            )}
            <div className="flex-1 min-w-[140px]">
              <p className="text-sm text-white font-medium">{p.title}</p>
              <p className="text-[11px] text-white/40 mt-0.5">
                {[p.location, p.category].filter(Boolean).join(" · ")}
              </p>
            </div>
            <span
              data-testid={`project-status-${i}`}
              className={`text-[9px] uppercase tracking-[0.2em] border px-2 py-1 ${
                p.published ? "border-[#c8b99d]/50 text-[#c8b99d]" : "border-white/20 text-white/40"
              }`}
            >
              {p.published ? "Published" : "Hidden"}
            </span>
            <div className="flex items-center gap-1.5">
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`project-up-${i}`} aria-label="Move up" className="border border-white/15 p-2 text-white/60 hover:text-white disabled:opacity-25">
                <ArrowUp size={13} />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === projects.length - 1} data-testid={`project-down-${i}`} aria-label="Move down" className="border border-white/15 p-2 text-white/60 hover:text-white disabled:opacity-25">
                <ArrowDown size={13} />
              </button>
              <button onClick={() => startEdit(p)} data-testid={`project-edit-${i}`} aria-label="Edit project" className="border border-white/15 p-2 text-white/60 hover:text-white">
                <Pencil size={13} />
              </button>
              <button onClick={() => togglePublished(p)} data-testid={`project-toggle-${i}`} aria-label="Toggle published" className="border border-white/15 p-2 text-white/60 hover:text-white">
                {p.published ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
              <button onClick={() => remove(p)} data-testid={`project-delete-${i}`} aria-label="Delete project" className="border border-white/15 p-2 text-white/60 hover:text-red-400">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const XSafe = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M1 1l8 8M9 1l-8 8" />
  </svg>
);

export default ProjectsTab;
